import clsx from 'clsx'
import first from 'lodash/first'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import noop from 'lodash/noop'
import { useRouter } from 'next/router'
import React, { FC, MutableRefObject, useCallback, useEffect, useMemo, useState } from 'react'

import { ampli } from '@Analytics/ampli'
import { recoverableCartProducts } from '@Analytics/helpers'
import SvgBin from '@Assets/SVG/Bin'
import SvgCheck from '@Assets/SVG/Check'
import SvgMinus from '@Assets/SVG/Minus'
import SvgPlus from '@Assets/SVG/Plus'
import { ImageV4 } from '@Components/Image'
import { Link } from '@Components/Link'
import { NativeModal, NativeModalActions, useNativeModalActions } from '@Components/Modal/NativeModal/NativeModal'
import { AddToWishList } from '@Components/WishList/addToWishList'
import { Button } from '@CoreComponents/Buttons/Button'
import { SizePromptV2 } from '@Domains/Cart/Components/Prompt/Size/SizePromptV2'
import { TextPersonalizationPrompt } from '@Domains/Cart/Components/Prompt/TextPersonalization/Prompt'
import { CartV2LineItem } from '@Domains/Cart/Feature/CartV2/CartV2'
import { LineItemPersonalization } from '@Domains/Cart/Feature/CartV2/Components/CartOverview/LineItem/Components/LineItemPersonalization'
import { getLineItemPersonalizations } from '@Domains/Cart/Feature/CartV2/Components/CartOverview/LineItem/utils/LineItemPersonalizationHelper'
import { getLineItemProperties } from '@Domains/Cart/Feature/CartV2/Components/CartOverview/LineItem/utils/LineItemPropertiesHelper'
import { useAddToCart } from '@Domains/Cart/Hooks/useAddToCart'
import {
  Product,
  ProductCrossSellAndPersonalizationContentFragment,
  useRemoveFromCartMutation,
  useSetQuantityInCartMutation,
  useSetVariantInCartMutation,
} from '@Generated/graphql'
import { useTranslation } from '@Globals/Translations'
import { Spinner } from '@Shared/Components/Spinner/Spinner'

import styles from './LineItem.module.scss'

export interface LineItemProps {
  additionAddedProducts?: string[]
  additionProducts?: Product[]
  bundleItemNumber?: number
  chips?: Array<'added'>
  className?: string
  giftWrappingIsAssociated?: boolean
  groupItem?: boolean
  isFinalized?: boolean
  isPartOfBundle?: boolean
  isPowerStep?: boolean
  lineItem: CartV2LineItem
  linkToProduct?: boolean
  onModalToggle?: (open: boolean) => void
  parent?: CartV2LineItem
  personalizationsLinkedToLineItem?: CartV2LineItem[]
  setMutatedCart?: () => void
  showAdditionProducts?: boolean
  showPersonalizationModifiers?: boolean
  showPersonalizeHeader?: boolean
  showPersonalizeImagesOnAddedProducts?: boolean
  showPersonalizeImagesOnUnaddedProducts?: boolean
  showQuantity?: boolean
  showQuantityBubble?: boolean
  showQuantityButtons?: boolean
  showRemoveButton?: boolean
  showUpsell?: boolean
  showWishListButton?: boolean
  v2?: boolean
}

const CartMutationOptions = {
  context: {
    domainProxy: true,
    userSpecific: true,
  },
}

export const LineItem: FC<LineItemProps> = ({
  additionProducts,
  additionAddedProducts = [],
  bundleItemNumber,
  chips = [],
  isPartOfBundle,
  linkToProduct = true,
  isFinalized = false,
  isPowerStep,
  giftWrappingIsAssociated,
  lineItem,
  parent,
  personalizationsLinkedToLineItem = [],
  setMutatedCart,
  showAdditionProducts = true,
  showPersonalizeHeader = true,
  className,
  groupItem,
  onModalToggle = noop,
  showPersonalizationModifiers = true,
  showPersonalizeImagesOnAddedProducts = true,
  showPersonalizeImagesOnUnaddedProducts = true,
  showQuantity = true,
  showQuantityBubble = false,
  showQuantityButtons = true,
  showRemoveButton,
  showUpsell = true,
  showWishListButton,
  v2,
}) => {
  const debossModalAction = useNativeModalActions()
  const engraveModalAction = useNativeModalActions()
  const sizeVariantAction = useNativeModalActions()
  const { t } = useTranslation()
  const [addedGiftWrapping, setAddedGiftWrapping] = useState(giftWrappingIsAssociated)
  const [addedAdditionProductIds, setAddedAdditionProductIds] = useState<string[]>(additionAddedProducts)
  const { locale = 'en-GB' } = useRouter()
  const [quantity, setquantity] = useState<number | null>(lineItem.quantity)
  const [showStockNotice, setShowStockNotice] = useState(false)
  const [
    addToCart, {
      isLoading: isLoadingAddToCart,
    },
  ] = useAddToCart({ sourceID: lineItem.id })

  const [setVariantInCart, { loading: variantLoading }] = useSetVariantInCartMutation(CartMutationOptions)

  const [
    removeFromCart, {
      loading: removeLoading,
    },
  ] = useRemoveFromCartMutation(CartMutationOptions)

  const [setQuantityInCart, { loading: quantityLoading }] = useSetQuantityInCartMutation(CartMutationOptions)
  const loading = useMemo(
    () => isLoadingAddToCart || variantLoading || quantityLoading || removeLoading,
    [isLoadingAddToCart, quantityLoading, removeLoading, variantLoading],
  )

  const ampliCartPersonalisationUpdated = (errors, noExistingPersonalization) => {
    ampli.cartPersonalisationUpdated({
      is_success: !isEmpty(errors),
      is_added: noExistingPersonalization,
      is_removed: true,
      is_updated: !noExistingPersonalization,
    })
  }

  useEffect(() => {
    if (showQuantityButtons) {
      setquantity(lineItem.quantity)
    }
  }, [lineItem.quantity, showQuantityButtons])

  const {
    canDeboss,
    canEngrave,
    canPersonalize,
    debossCrossSellPrice,
    debossCrossSells,
    debossLineItem,
    debossPrice,
    debossText,
    engraveCrossSellPrice,
    engraveCrossSells,
    engraveLineItem,
    engravePrice,
    engravedText,
    isDebossed,
    isEngraved,
    isPersonalized,
  } = useMemo(
    () => getLineItemPersonalizations(lineItem, personalizationsLinkedToLineItem, locale),
    [lineItem, locale, personalizationsLinkedToLineItem],
  )

  const {
    freeGiftItemDisableSizeSelection,
    freeGiftItemHasSelectedVariant,
    freeGiftItemIsSizable,
    getSizeLabel,
    giftWrapping,
    hasSize,
    isDiscounted,
    isFree,
    isFreeGiftItem,
    isQuantifiable,
    isSizeable,
    normalPriceIsZero,
    productName,
    quantityAvailable,
    sizeSelected,
    totalPriceDiscountFormat,
    totalPriceFormat,
  } = useMemo(
    () => getLineItemProperties(lineItem, locale, { parent }),
    [lineItem, locale, parent],
  )

  const removePersonalization = useCallback((crossSells: ProductCrossSellAndPersonalizationContentFragment[]) => {
    const personalization = personalizationsLinkedToLineItem.find(item1 => crossSells.some(item2 => item1.productID === item2.id))
    if (personalization) {
      removeFromCart({ variables: { lineItemID: personalization?.id } })
        .then((a) => {
          ampli.cartRemoveRes({
            is_success: isEmpty(a.errors),
          })
          return a
        })
    }
  }, [personalizationsLinkedToLineItem, removeFromCart])

  const updateQuantity = useCallback((newQuantity: number, buttonsUsed: boolean) => {
    if (!setMutatedCart) {
      return
    }
    setMutatedCart()
    if (newQuantity === 0) {
      removeFromCart({ variables: { lineItemID: parent?.id || lineItem.id } })
        .then((a) => {
          ampli.cartRemoveRes({
            is_success: isEmpty(a.errors),
            recoverable_cart_products: recoverableCartProducts(a.data?.removeFromCart?.cart),
          })
          return a
        })
    } else {
      const oldQuantity = lineItem.quantity
      setQuantityInCart({
        variables: {
          lineItems: [
            {
              lineItemID: lineItem.id,
              quantity: newQuantity,
            },
          ],
        },
      }).then((resp) => {
        if (oldQuantity) {
          ampli.cartQtyChange({
            is_quantity_increased: oldQuantity < newQuantity,
            was_buttons_used: buttonsUsed,
            recoverable_cart_products: recoverableCartProducts(resp.data?.setQuantityInCart?.cart),
          })
        }
      }).catch(() => {
        if (oldQuantity) {
          ampli.cartQtyChange({
            is_quantity_increased: oldQuantity < newQuantity,
            was_buttons_used: buttonsUsed,
          })
        }
      })
    }
  }, [lineItem.id, lineItem.quantity, parent?.id, removeFromCart, setMutatedCart, setQuantityInCart])

  const handleChange = (value: string, submit?: boolean) => {
    if (value === '') {
      if (submit) {
        setquantity(1)
        updateQuantity(1, true)
      } else {
        setquantity(null)
      }
      return
    }

    const number = parseInt(value, 10)

    if (Number.isNaN(number)) {
      return
    }

    setShowStockNotice(number > (quantityAvailable))

    if (number > (quantityAvailable)) {
      setquantity(quantityAvailable)
    } else if (number < 0) {
      setquantity(0)
    } else if (number > 9999) {
      setquantity(9999)
    } else {
      setquantity(number)
    }

    if (submit) {
      updateQuantity(number, true)
    }
  }

  const toggleModal = useCallback((modalState: 'open' | 'close', actionRef: MutableRefObject<NativeModalActions | null>, update?: boolean) => {
    if (modalState === 'open') {
      onModalToggle(true)
      actionRef.current?.open()
      if (update) {
        ampli.cartPersonalisationUpdateClick({
          source_id: 'cart',
        })
      } else {
        ampli.cartPersonalisationAddClick({
          source_id: 'cart',
        })
      }
    } else {
      onModalToggle(false)
      actionRef.current?.close()
    }
  }, [onModalToggle])
  const getPersonalizationImage = useCallback((crossSelles, showPersonalizeImages) => {
    if (showPersonalizeImages && crossSelles) {
      return (
        crossSelles.find(item1 => personalizationsLinkedToLineItem.some(item2 => item1.id === item2.productID))
        || first(crossSelles)
      )?.primaryAsset?.url
    }
    return undefined
  }, [personalizationsLinkedToLineItem])

  const imageHeight = useMemo(() => {
    if (v2) {
      return 90
    }

    return 96
  }, [v2])

  const imageWidth = imageHeight

  if ((isPartOfBundle && !parent) || !lineItem.productID || !lineItem.product) {
    return null
  }

  return (
    <div className={clsx(
      styles.lineItemWrapper,
      v2 && styles.lineItemWrapperV2,
      v2 && showPersonalizeHeader && (
        (canPersonalize)
                || (giftWrapping)
                || (!isEmpty(additionProducts?.filter(a => !addedAdditionProductIds.includes(a.id))))
      ) && styles.lineItemShowPersonalizations,
      v2 && isPowerStep && styles.lineItemPowerStep,
      className,
    )}
    >
      <div className={clsx(loading && styles.loading)} style={{ display: 'none' }}>
        <Spinner />
      </div>
      <div className={styles.lineItem}>
        {lineItem.product.primaryAsset?.url && (
          <div className={styles.imageWrapper}>
            <div className={styles.image}>
              <ImageV4
                alt={lineItem.product.name || ''}
                height={imageHeight}
                src={lineItem.product.primaryAsset?.url}
                width={imageWidth}
              />
            </div>
          </div>
        )}
        {v2 && isQuantifiable && showQuantity && !showQuantityBubble && (
          <span className={clsx(styles.isQuantifiable, isFreeGiftItem && styles.hide)}>
            {showQuantityButtons && isQuantifiable && (
            // eslint-disable-next-line jsx-a11y/control-has-associated-label
              <button
                onClick={() => handleChange(`${isQuantifiable ? lineItem.quantity - 1 : 0}`, true)}
                type="button"
              >
                <SvgMinus />
              </button>
            )}
            {showQuantity && isQuantifiable && (
              <input
                className={clsx(showQuantityBubble && styles.inputBubble)}
                disabled={showQuantityBubble || !isQuantifiable}
                inputMode="numeric"
                max={9999}
                onBlur={a => handleChange(a.target.value, true)}
                onChange={a => handleChange(a.target.value)}
                onKeyUp={(e) => {
                  if (e.key === 'Enter') {
                    e.currentTarget.blur()
                  }
                }}
                type={showQuantityBubble ? 'text' : 'number'}
                value={showQuantityBubble ? `${quantity}x` : quantity || ''}
              />
            )}
            {showQuantityButtons && isQuantifiable && (
            // eslint-disable-next-line jsx-a11y/control-has-associated-label
              <button
                disabled={lineItem.quantity === quantityAvailable}
                onClick={() => handleChange(`${lineItem.quantity + 1}`, true)}
                type="button"
              >
                <SvgPlus />
              </button>
            )}
          </span>
        )}
        <div className={styles.wishAndForget}>
          {showRemoveButton && (
            <Button className={styles.wishForgetItem} innerSpacing="small" onClick={() => handleChange('0', true)} theme="square">
              <SvgBin />
            </Button>
          )}
          {showWishListButton && (
            <AddToWishList className={clsx(styles.wishForgetItem, styles.wishButton)} id={lineItem.product.id} />
          )}
        </div>
        <div className={styles.productInfo}>
          {v2 && isDiscounted && !groupItem && lineItem.discountText && <span className={styles.discountText}>{lineItem.discountText}</span>}
          {v2 && groupItem && bundleItemNumber && (
            <span className={styles.discountText}>
              {bundleItemNumber}
            </span>
          )}
          {chips.map(chip => chip === 'added' && (
            <p key={`chip_${chip}`} className={clsx(styles.chip, styles.added)}>
              {t('products:ADDED_TO_CART')}
              <SvgCheck />
            </p>
          ))}
          {linkToProduct && <Link className={styles.productName} href={lineItem.product.url!}>{productName}</Link>}
          {!linkToProduct && <p className={styles.productName}>{productName}</p>}
          {hasSize && (<p className={styles.sizeText}>{`${t('cms:SIZE')}: ${getSizeLabel(lineItem)}`}</p>)}
          {(!v2 || (v2 && showQuantityBubble)) && (
            <span className={clsx(styles.isQuantifiable, isFreeGiftItem && styles.hide)}>
              {showQuantityButtons && isQuantifiable && (
              // eslint-disable-next-line jsx-a11y/control-has-associated-label
                <button
                  onClick={() => handleChange(`${isQuantifiable ? lineItem.quantity - 1 : 0}`, true)}
                  type="button"
                >
                  <SvgMinus />
                </button>
              )}
              {showQuantity && isQuantifiable && (
                <input
                  className={clsx(showQuantityBubble && styles.inputBubble)}
                  disabled={showQuantityBubble || !isQuantifiable}
                  inputMode="numeric"
                  max={9999}
                  onBlur={a => handleChange(a.target.value, true)}
                  onChange={a => handleChange(a.target.value)}
                  onKeyUp={(e) => {
                    if (e.key === 'Enter') {
                      e.currentTarget.blur()
                    }
                  }}
                  type={showQuantityBubble ? 'text' : 'number'}
                  value={showQuantityBubble ? `${quantity}x` : quantity || ''}
                />
              )}
              {showQuantityButtons && isQuantifiable && (
              // eslint-disable-next-line jsx-a11y/control-has-associated-label
                <button
                  disabled={lineItem.quantity === quantityAvailable}
                  onClick={() => handleChange(`${lineItem.quantity + 1}`, true)}
                  type="button"
                >
                  <SvgPlus />
                </button>
              )}
            </span>
          )}
          {((!v2 && showStockNotice) || (v2 && showStockNotice && isPowerStep)) && (
            <span className={styles.stockNotice}>
              {`${quantityAvailable} ${t('products:PP_LEFT_IN_STOCK_LABEL')}`}
            </span>
          )}
          {!isFinalized && isSizeable && !hasSize && (
            <button
              className={clsx(
                styles.sizeSelect,
                showUpsell && isFreeGiftItem && !freeGiftItemHasSelectedVariant && styles.missSizeOnGiftItem,
                showUpsell && isFreeGiftItem && freeGiftItemHasSelectedVariant && styles.isFreeGiftItem,
              )}
              disabled={!showUpsell || Boolean(isFreeGiftItem && freeGiftItemDisableSizeSelection)}
              onClick={() => sizeVariantAction.current?.open()}
              type="button"
            >
              {showUpsell && isFreeGiftItem && !freeGiftItemHasSelectedVariant && t('cart:SELECT_SIZE')}
              {showUpsell && isFreeGiftItem && freeGiftItemHasSelectedVariant && (
                <>
                  {`${t('cms:SIZE')}: `}
                  {(getSizeLabel(lineItem) || (sizeSelected?.availability?.isInStock ? '' : t('cms:OUT_OF_STOCK')))}
                  {showUpsell && (isFreeGiftItem ? !freeGiftItemDisableSizeSelection : true) && (
                    <>
                      {' - '}
                      <strong>
                        {t('cart:CHANGE')}
                      </strong>
                    </>
                  )}
                </>
              )}
              {(!showUpsell || !isFreeGiftItem) && (getSizeLabel(lineItem) || (sizeSelected?.availability?.isInStock ? '' : t('cms:OUT_OF_STOCK')))}
            </button>
          )}
          {!isPowerStep && isFreeGiftItem && showUpsell && !showRemoveButton && (
            <button className={styles.delete} onClick={() => handleChange('0', true)} type="button">
              {v2 ? t('common:TRENDHIMCLUB_REMOVE_LABEL') : <SvgBin />}
            </button>
          )}
          {showUpsell && isFreeGiftItem && !freeGiftItemHasSelectedVariant && freeGiftItemIsSizable && !freeGiftItemDisableSizeSelection && (
            <span className={clsx(styles.freeItemSizeError, 'lineItem_error')}>
              {t('cart:REMEMBER_TO_CHOOSE_WHAT_SIZE_YOU_WOULD_LIKE')}
            </span>
          )}

        </div>
        <div className={styles.priceWrapper}>
          {!v2 && isDiscounted && !groupItem && lineItem.discountText && <span className={styles.discountText}>{lineItem.discountText}</span>}
          <div className={styles.prices}>
            {!groupItem && !isFree && totalPriceDiscountFormat && <p className={styles.discountPrice}>{totalPriceDiscountFormat}</p>}
            {isFree && <p className={styles.freePrice}>{t('header:FREE_LABEL')}</p>}
            {!groupItem && !normalPriceIsZero && totalPriceFormat && <p className={styles.originalPrice}>{totalPriceFormat}</p>}
          </div>
        </div>
        {v2 && !isPowerStep && (
          <div className={clsx(showPersonalizeHeader && (
            (canPersonalize)
                || (giftWrapping)
                || (!isEmpty(additionProducts?.filter(a => !addedAdditionProductIds.includes(a.id))))
          ) && styles.lineItemPersonalizations, styles.lineItemPersonalizations)}
          >
            {isEngraved && engraveLineItem && engravePrice
          && (
            <LineItemPersonalization
              discountPrice={engravePrice.discountPrice}
              imageUrl={getPersonalizationImage(engraveCrossSells, showPersonalizeImagesOnAddedProducts)}
              isFree={engravePrice.isFree}
              onEdit={showUpsell && showPersonalizationModifiers ? () => toggleModal('open', engraveModalAction, true) : undefined}
              onRemove={showUpsell && showPersonalizationModifiers ? () => removePersonalization(engraveCrossSells) : undefined}
              originalPrice={isPowerStep ? '' : engravePrice.price}
              showCheckBox={isEngraved && isPowerStep}
              text={`${engraveLineItem.shortName}, ${engravedText}`}
              title={t('products:ENGRAVING')}
              v2={v2}
            />
          )}
            {isDebossed && debossLineItem && debossPrice
          && (
            <LineItemPersonalization
              discountPrice={debossPrice.discountPrice}
              imageUrl={getPersonalizationImage(debossCrossSells, showPersonalizeImagesOnAddedProducts)}
              isFree={debossPrice.isFree}
              onEdit={showUpsell && showPersonalizationModifiers ? () => toggleModal('open', debossModalAction, true) : undefined}
              onRemove={showUpsell && showPersonalizationModifiers ? () => removePersonalization(debossCrossSells) : undefined}
              originalPrice={debossPrice.price}
              text={`${debossLineItem.shortName}, ${debossText}`}
              title={t('products:DEBOSSING')}
              v2={v2}
            />
          )}
            {giftWrapping && addedGiftWrapping && (
              <LineItemPersonalization
                discountPrice={giftWrapping.masterVariant?.price?.discount?.format || ''}
                hideRemove
                imageUrl={
                  (addedGiftWrapping ? showPersonalizeImagesOnAddedProducts : showPersonalizeImagesOnUnaddedProducts)
                    ? giftWrapping.asset?.url
                    : undefined
                }
                isFree={giftWrapping.masterVariant?.price?.discount?.value === 0 || giftWrapping.masterVariant?.price?.value === 0}
                onRemove={noop}
                showCheckBox={addedGiftWrapping}
                title={t('cart:GIFT_PACKAGE_ADDED')}
                v2={v2}
              />
            )}
            {showAdditionProducts && map(additionProducts?.filter(a => addedAdditionProductIds.includes(a.id)), additionProduct => (
              <LineItemPersonalization
                key={`addedAdditionProductIds_${additionProduct.id}`}
                hideRemove
                imageUrl={(showPersonalizeImagesOnAddedProducts && (
                  additionProduct.asset?.url || first(additionProduct.assets)?.url
                )) || undefined}
                isFree={additionProduct.masterVariant?.price?.discount?.value === 0 || additionProduct.masterVariant?.price?.value === 0}
                onRemove={noop}
                showCheckBox
                title={additionProduct.name || ''}
                v2={v2}
              />
            ))}
            {!v2 && showPersonalizeHeader && (
              (canPersonalize && !isPersonalized)
                || (!addedGiftWrapping && giftWrapping)
                || (!isEmpty(additionProducts?.filter(a => !addedAdditionProductIds.includes(a.id))))
            )
          && (
            <p className={clsx(styles.personalizeHeader, (addedGiftWrapping || isPersonalized) && styles.marginTop)}>
              {t('products:WB_UPGRADE_WITH')}
            </p>
          )}
            {showUpsell && canEngrave && !isPersonalized && !isEngraved && engraveCrossSellPrice
          && (
            <LineItemPersonalization
              discountPrice={engraveCrossSellPrice.discountPrice}
              imageUrl={showPersonalizeImagesOnUnaddedProducts ? first(engraveCrossSells)?.primaryAsset?.url : undefined}
              isFree={engraveCrossSellPrice.isFree}
              onAdd={() => toggleModal('open', engraveModalAction, false)}
              originalPrice={engraveCrossSellPrice.price}
              title={t('cart:ADD_ENGRAVING')}
              v2={v2}
            />
          )}
            {showUpsell && canDeboss && !isPersonalized && !isDebossed && debossCrossSellPrice
          && (
            <LineItemPersonalization
              discountPrice={debossCrossSellPrice.discountPrice}
              imageUrl={showPersonalizeImagesOnUnaddedProducts ? first(debossCrossSells)?.primaryAsset?.url : undefined}
              isFree={debossCrossSellPrice.isFree}
              onAdd={() => toggleModal('open', debossModalAction, false)}
              originalPrice={debossCrossSellPrice.price}
              title={t('cart:ADD_DEBOSSING')}
              v2={v2}
            />
          )}
            {showUpsell && giftWrapping && !addedGiftWrapping && (
              <LineItemPersonalization
                discountPrice={giftWrapping.masterVariant?.price?.discount?.format || ''}
                imageUrl={
                  (addedGiftWrapping ? showPersonalizeImagesOnAddedProducts : showPersonalizeImagesOnUnaddedProducts)
                    ? giftWrapping.asset?.url
                    : undefined
                }
                isFree={giftWrapping.masterVariant?.price?.discount?.value === 0 || giftWrapping.masterVariant?.price?.value === 0}
                onAdd={() => {
                  addToCart([
                    {
                      productID: giftWrapping.id,
                      quantity: 1,
                    },
                  ], {
                    sourceID: 'high_cross_sales',
                    skipReset: true,
                    withoutSuccessPrompt: true,
                  })
                    .then((response) => {
                      if (response?.data?.addToCart?.success) {
                        setAddedGiftWrapping(true)
                      }
                    })
                }}
                originalPrice={giftWrapping.masterVariant?.price?.format}
                showCheckBox={addedGiftWrapping}
                title={t('products:ADD_GIFT_PACKAGING')}
                v2={v2}
              />
            )}
            {showAdditionProducts && map(additionProducts?.filter(a => !addedAdditionProductIds.includes(a.id)), additionProduct => (
              <LineItemPersonalization
                key={`addingAdditionProductIds_${additionProduct.id}`}
                discountPrice={additionProduct.masterVariant?.price?.discount?.format || ''}
                imageUrl={additionProduct.asset?.url || first(additionProduct.assets)?.url || undefined}
                onAdd={() => {
                  addToCart([{ productID: additionProduct.id, quantity: 1 }]).then((response) => {
                    if (response?.data?.addToCart?.success) {
                      setAddedAdditionProductIds(prev => [...prev, additionProduct.id])
                    }
                  })
                }}
                originalPrice={additionProduct.masterVariant?.price?.format || ''}
                title={additionProduct.name || ''}
                v2={v2}
              />
            ))}
          </div>
        )}
        {v2 && !isPowerStep && showStockNotice && (
          <span className={styles.stockNotice}>
            {`${quantityAvailable} ${t('products:PP_LEFT_IN_STOCK_LABEL')}`}
          </span>
        )}
      </div>
      {((v2 && isPowerStep) || !v2) && (
        <div className={clsx(showPersonalizeHeader && (
          (canPersonalize)
                || (giftWrapping)
                || (!isEmpty(additionProducts?.filter(a => !addedAdditionProductIds.includes(a.id))))
        ) && styles.lineItemPersonalizations, showPersonalizeHeader && v2 && 'fullwidth')}
        >
          {v2 && showPersonalizeHeader && (
            (canPersonalize && !isPersonalized)
                || (!addedGiftWrapping && giftWrapping)
                || (!isEmpty(additionProducts?.filter(a => !addedAdditionProductIds.includes(a.id))))
          )
          && (
            <p className={clsx(styles.personalizeHeader, (addedGiftWrapping || isPersonalized) && styles.marginTop)}>
              {t('products:WB_UPGRADE_WITH')}
            </p>
          )}
          {isEngraved && engraveLineItem && engravePrice
          && (
            <LineItemPersonalization
              discountPrice={engravePrice.discountPrice}
              imageUrl={getPersonalizationImage(engraveCrossSells, v2 ? true : showPersonalizeImagesOnAddedProducts)}
              isFree={engravePrice.isFree}
              isPowerStep
              onEdit={showUpsell && showPersonalizationModifiers ? () => toggleModal('open', engraveModalAction, true) : undefined}
              onRemove={showUpsell && showPersonalizationModifiers ? () => removePersonalization(engraveCrossSells) : undefined}
              originalPrice={isPowerStep ? '' : engravePrice.price}
              showCheckBox={isEngraved && isPowerStep}
              text={`${engraveLineItem.shortName}, ${engravedText}`}
              title={t('products:ENGRAVING')}
              v2={v2}
            />
          )}
          {isDebossed && debossLineItem && debossPrice
          && (
            <LineItemPersonalization
              discountPrice={debossPrice.discountPrice}
              imageUrl={getPersonalizationImage(debossCrossSells, v2 ? true : showPersonalizeImagesOnAddedProducts)}
              isFree={debossPrice.isFree}
              isPowerStep
              onEdit={showUpsell && showPersonalizationModifiers ? () => toggleModal('open', debossModalAction, true) : undefined}
              onRemove={showUpsell && showPersonalizationModifiers ? () => removePersonalization(debossCrossSells) : undefined}
              originalPrice={debossPrice.price}
              text={`${debossLineItem.shortName}, ${debossText}`}
              title={t('products:DEBOSSING')}
              v2={v2}
            />
          )}
          {giftWrapping && addedGiftWrapping && (
            <LineItemPersonalization
              discountPrice={giftWrapping.masterVariant?.price?.discount?.format || ''}
              hideRemove
              imageUrl={
                  (() => {
                    let showImage = false
                    if (v2) {
                      showImage = true
                    } else if (addedGiftWrapping) {
                      showImage = showPersonalizeImagesOnAddedProducts
                    } else {
                      showImage = showPersonalizeImagesOnUnaddedProducts
                    }
                    return showImage ? giftWrapping.asset?.url : undefined
                  })()
                }
              isFree={giftWrapping.masterVariant?.price?.discount?.value === 0 || giftWrapping.masterVariant?.price?.value === 0}
              isPowerStep
              onRemove={noop}
              showAsLineItem={!showPersonalizeHeader}
              showCheckBox={addedGiftWrapping}
              title={t('cart:GIFT_PACKAGE_ADDED')}
              v2={v2}
            />
          )}
          {showAdditionProducts && map(additionProducts?.filter(a => addedAdditionProductIds.includes(a.id)), additionProduct => (
            <LineItemPersonalization
              key={`addedAdditionProductIds_${additionProduct.id}`}
              hideRemove
              imageUrl={(showPersonalizeImagesOnAddedProducts && (
                additionProduct.asset?.url || first(additionProduct.assets)?.url
              )) || undefined}
              isFree={additionProduct.masterVariant?.price?.discount?.value === 0 || additionProduct.masterVariant?.price?.value === 0}
              isPowerStep
              onRemove={noop}
              showCheckBox
              title={additionProduct.name || ''}
              v2={v2}
            />
          ))}
          {!v2 && showPersonalizeHeader && (
            (canPersonalize && !isPersonalized)
                || (!addedGiftWrapping && giftWrapping)
                || (!isEmpty(additionProducts?.filter(a => !addedAdditionProductIds.includes(a.id))))
          )
          && (
            <p className={clsx(styles.personalizeHeader, (addedGiftWrapping || isPersonalized) && styles.marginTop)}>
              {t('products:WB_UPGRADE_WITH')}
            </p>
          )}
          {showUpsell && canEngrave && !isPersonalized && !isEngraved && engraveCrossSellPrice
          && (
            <LineItemPersonalization
              discountPrice={engraveCrossSellPrice.discountPrice}
              imageUrl={showPersonalizeImagesOnUnaddedProducts ? first(engraveCrossSells)?.primaryAsset?.url : undefined}
              isFree={engraveCrossSellPrice.isFree}
              isPowerStep
              onAdd={() => toggleModal('open', engraveModalAction, false)}
              originalPrice={engraveCrossSellPrice.price}
              title={t('cart:ADD_ENGRAVING')}
              v2={v2}
            />
          )}
          {showUpsell && canDeboss && !isPersonalized && !isDebossed && debossCrossSellPrice
          && (
            <LineItemPersonalization
              discountPrice={debossCrossSellPrice.discountPrice}
              imageUrl={showPersonalizeImagesOnUnaddedProducts ? first(debossCrossSells)?.primaryAsset?.url : undefined}
              isFree={debossCrossSellPrice.isFree}
              isPowerStep
              onAdd={() => toggleModal('open', debossModalAction, false)}
              originalPrice={debossCrossSellPrice.price}
              title={t('cart:ADD_DEBOSSING')}
              v2={v2}
            />
          )}
          {showUpsell && giftWrapping && !addedGiftWrapping && (
            <LineItemPersonalization
              discountPrice={giftWrapping.masterVariant?.price?.discount?.format || ''}
              imageUrl={
                  (addedGiftWrapping ? showPersonalizeImagesOnAddedProducts : showPersonalizeImagesOnUnaddedProducts)
                    ? giftWrapping.asset?.url
                    : undefined
                }
              isFree={giftWrapping.masterVariant?.price?.discount?.value === 0 || giftWrapping.masterVariant?.price?.value === 0}
              isPowerStep
              onAdd={() => {
                addToCart([
                  {
                    productID: giftWrapping.id,
                    quantity: 1,
                  },
                ], {
                  sourceID: 'high_cross_sales',
                  skipReset: true,
                  withoutSuccessPrompt: true,
                })
                  .then((response) => {
                    if (response?.data?.addToCart?.success) {
                      setAddedGiftWrapping(true)
                    }
                  })
              }}
              originalPrice={giftWrapping.masterVariant?.price?.format}
              showCheckBox={addedGiftWrapping}
              title={t('products:ADD_GIFT_PACKAGING')}
              v2={v2}
            />
          )}
          {showAdditionProducts && map(additionProducts?.filter(a => !addedAdditionProductIds.includes(a.id)), additionProduct => (
            <LineItemPersonalization
              key={`addingAdditionProductIds_${additionProduct.id}`}
              discountPrice={additionProduct.masterVariant?.price?.discount?.format || ''}
              imageUrl={additionProduct.asset?.url || first(additionProduct.assets)?.url || undefined}
              isPowerStep
              onAdd={() => {
                addToCart([{ productID: additionProduct.id, quantity: 1 }]).then((response) => {
                  if (response?.data?.addToCart?.success) {
                    setAddedAdditionProductIds(prev => [...prev, additionProduct.id])
                  }
                })
              }}
              originalPrice={additionProduct.masterVariant?.price?.format || ''}
              title={additionProduct.name || ''}
              v2={v2}
            />
          ))}
        </div>
      )}

      {debossCrossSells.length !== 0 && (
        <NativeModal
          actionRef={debossModalAction}
          alignment="right"
          backdrop
          className={styles.modal}
          onClose={() => toggleModal('close', debossModalAction)}
        >
          <TextPersonalizationPrompt
            availablePersonalizations={debossCrossSells}
            initialValue={(debossLineItem?.lineItem?.type as any)?.text || undefined}
            onSubmit={async (productID, personalization) => {
              const noExistingDebossing = isEmpty(debossLineItem)

              if (!productID && debossLineItem?.lineItem?.id) {
                return removeFromCart({ variables: { lineItemID: debossLineItem.lineItem.id } })
                  .then((a) => {
                    ampliCartPersonalisationUpdated(a.errors, noExistingDebossing)
                    return a
                  })
                  .then(() => toggleModal('close', debossModalAction))
              }

              if (productID) {
                return addToCart(
                  [
                    {
                      parentLineItemID: lineItem.id,
                      productID,
                      // @ts-ignore
                      personalization: { text: personalization.text },
                    },
                  ],
                  {
                    skipReset: true,
                    withoutSuccessPrompt: true,
                  },
                )
                  .then(a => ampliCartPersonalisationUpdated(a?.errors, noExistingDebossing))
                  .then(() => toggleModal('close', debossModalAction))
              }

              return toggleModal('close', debossModalAction)
            }}
            selectedPersonalization={
              debossCrossSells.find(item1 => personalizationsLinkedToLineItem.some(item2 => item1.id === item2.productID))
              || first(debossCrossSells)
              || null
            }
            type="deboss"
            useUpdateFlow
          />
        </NativeModal>
      )}

      {engraveCrossSells.length !== 0 && (
        <NativeModal
          actionRef={engraveModalAction}
          alignment="right"
          backdrop
          className={styles.modal}
          onClose={() => toggleModal('close', engraveModalAction)}
        >
          <TextPersonalizationPrompt
            availablePersonalizations={engraveCrossSells}
            initialValue={(engraveLineItem?.lineItem?.type as any)?.text || undefined}
            onSubmit={async (productID, personalization) => {
              const noExistingEngraving = isEmpty(engraveLineItem)

              if (!productID && engraveLineItem?.lineItem?.id) {
                return removeFromCart({ variables: { lineItemID: engraveLineItem.lineItem.id } })
                  .then((a) => {
                    ampliCartPersonalisationUpdated(a.errors, noExistingEngraving)
                    return a
                  })
                  .then(() => toggleModal('close', engraveModalAction))
              }

              if (productID) {
                return addToCart(
                  [
                    {
                      parentLineItemID: lineItem.id,
                      productID,
                      // @ts-ignore
                      personalization: { text: personalization.text },
                    },
                  ],
                  {
                    skipReset: true,
                    withoutSuccessPrompt: true,
                  },
                )
                  .then(a => ampliCartPersonalisationUpdated(a?.errors, noExistingEngraving))
                  .then(() => toggleModal('close', engraveModalAction))
              }

              return toggleModal('close', engraveModalAction)
            }}
            selectedPersonalization={
              engraveCrossSells.find(item1 => personalizationsLinkedToLineItem.some(item2 => item1.id === item2.productID))
              || first(engraveCrossSells)
              || null
            }
            type="engrave"
            useUpdateFlow
          />
        </NativeModal>
      )}
      {isSizeable && !hasSize && lineItem.product && (
        <NativeModal
          actionRef={sizeVariantAction}
          alignment="right"
          backdrop
          className={styles.modal}
          onClose={() => toggleModal('close', sizeVariantAction)}
        >
          <SizePromptV2
            loading={isLoadingAddToCart}
            onSubmit={(variant) => {
              if (variant.variantID) {
                setVariantInCart({
                  variables: {
                    lineItems: [
                      {
                        variantID: variant.variantID,
                        lineItemID: lineItem.id,
                      },
                    ],
                  },
                })
              }
              sizeVariantAction.current?.close()
            }}
            product={lineItem.product as Product}
          />
        </NativeModal>
      )}
    </div>
  )
}

